import { useMemo, useState } from "react";

import type { GithubOrgRepo } from "@oracus/shared/api/github";
import type { SourceRepoSummary } from "@oracus/shared/api/repos";

import {
  useAddComponentMapEntry,
  useComponentMap,
  useDeleteComponentMapEntry,
} from "../api/component-map.api.ts";
import {
  useGithubIntegration,
  useGithubOrgRepos,
  useImportGithubRepos,
} from "../api/github.api.ts";
import {
  useAddRepo,
  useDeleteRepo,
  useRepoSpecs,
  useRepos,
  useSyncRepo,
  useUpdateRepo,
} from "../api/repos.api.ts";
import { useTeams } from "../api/teams.api.ts";
import { Badge, Button, Card, Page } from "../components/Page.tsx";

type AuthMethod = "none" | "pat";
type SourceKind = "git" | "local";

export function Repositories() {
  const repos = useRepos();
  const add = useAddRepo();

  const [kind, setKind] = useState<SourceKind>("git");
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [authMethod, setAuthMethod] = useState<AuthMethod>("none");
  const [pat, setPat] = useState("");

  const isLocal = kind === "local";
  const canSubmit =
    url.trim().length > 0 &&
    !add.isPending &&
    (isLocal || authMethod !== "pat" || pat.trim().length > 0);

  return (
    <Page
      title="Repositories"
      description="Connect git repositories. Oracus clones each one, runs spec-gen to extract OpenSpec specs, embeds them, and indexes them alongside your other sources."
    >
      <GithubOrgImportCard />

      <div className="mt-6" />

      <Card title="Add a repository (single URL or local path)">
        <p className="text-sm text-zinc-600">
          Connect a Git repository (HTTPS) or point at a local path on this
          machine. Local sources are not cloned — spec-gen runs in-place and
          writes <code>.spec-gen/</code> + <code>openspec/</code> directories
          inside the path.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="text-zinc-700">Source</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as SourceKind)}
              className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            >
              <option value="git">Git URL (HTTPS)</option>
              <option value="local">Local filesystem path</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-zinc-700">
              {isLocal ? "Absolute path" : "Repository URL"}
            </span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={
                isLocal
                  ? "/Users/you/Sites/my-private-repo"
                  : "https://github.com/owner/repo"
              }
              className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 font-mono text-sm"
            />
            {isLocal && (
              <span className="mt-1 block text-xs text-zinc-500">
                spec-gen will write <code>.spec-gen/</code> and{" "}
                <code>openspec/</code> here. Add them to <code>.gitignore</code>{" "}
                if needed.
              </span>
            )}
          </label>
          <label className="block text-sm">
            <span className="text-zinc-700">
              Display name{" "}
              <span className="text-xs text-zinc-500">
                (optional — derived from {isLocal ? "path" : "URL"} if blank)
              </span>
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isLocal ? "my-private-repo" : "owner/repo"}
              className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            />
          </label>
          {!isLocal && (
            <>
              <label className="block text-sm">
                <span className="text-zinc-700">Auth</span>
                <select
                  value={authMethod}
                  onChange={(e) => setAuthMethod(e.target.value as AuthMethod)}
                  className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="none">None (public repo)</option>
                  <option value="pat">Personal Access Token</option>
                </select>
              </label>
              {authMethod === "pat" && (
                <label className="block text-sm">
                  <span className="text-zinc-700">PAT</span>
                  <input
                    type="password"
                    value={pat}
                    onChange={(e) => setPat(e.target.value)}
                    placeholder="ghp_... or github_pat_..."
                    className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 font-mono text-sm"
                  />
                </label>
              )}
            </>
          )}
          <Button
            disabled={!canSubmit}
            onClick={() => {
              const submitAuthMethod = isLocal ? "none" : authMethod;
              add.mutate(
                {
                  kind,
                  url: url.trim(),
                  ...(name.trim() ? { name: name.trim() } : {}),
                  authMethod: submitAuthMethod,
                  ...(submitAuthMethod === "pat"
                    ? { authCredential: pat.trim() }
                    : {}),
                },
                {
                  onSuccess: () => {
                    setUrl("");
                    setName("");
                    setPat("");
                    setAuthMethod("none");
                    setKind("git");
                  },
                },
              );
            }}
          >
            {add.isPending ? "Adding…" : "Add and ingest"}
          </Button>
          {add.error && (
            <p className="text-xs text-red-700">
              {String((add.error as Error).message)}
            </p>
          )}
        </div>
      </Card>

      <div className="mt-6">
        <Card title="Connected repositories">
          {repos.isLoading && (
            <p className="text-sm text-zinc-500">Loading…</p>
          )}
          {repos.data?.repos.length === 0 && (
            <p className="text-sm text-zinc-500">
              None yet. Add one above to kick off ingestion.
            </p>
          )}
          <ul className="divide-y divide-zinc-100">
            {(repos.data?.repos ?? []).map((repo) => (
              <RepoRow key={repo.id} repo={repo} />
            ))}
          </ul>
        </Card>
      </div>
    </Page>
  );
}

function ComponentMapPanel({ repoId }: { repoId: string }) {
  const entries = useComponentMap(repoId, true);
  const add = useAddComponentMapEntry(repoId);
  const remove = useDeleteComponentMapEntry(repoId);
  const [draft, setDraft] = useState("");

  const submit = () => {
    const name = draft.trim();
    if (!name || add.isPending) return;
    add.mutate(
      { componentName: name, source: "jira" },
      { onSuccess: () => setDraft("") },
    );
  };

  return (
    <div className="rounded border border-zinc-200 bg-white p-3 text-sm">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
          Component / path tags
        </span>
        <span className="text-xs text-zinc-500">
          Auto-links matching Jira tickets &amp; JUnit tests to this repo
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {entries.isLoading && (
          <span className="text-xs text-zinc-500">Loading…</span>
        )}
        {entries.data?.entries.length === 0 && (
          <span className="text-xs text-zinc-500">
            None yet. Add Jira component names or test-path segments below.
          </span>
        )}
        {(entries.data?.entries ?? []).map((e) => (
          <span
            key={e.id}
            className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700"
          >
            {e.componentName}
            <button
              type="button"
              onClick={() => remove.mutate(e.id)}
              className="text-zinc-500 hover:text-zinc-900"
              title="Remove"
              aria-label={`Remove ${e.componentName}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          value={draft}
          onChange={(ev) => setDraft(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              submit();
            }
          }}
          placeholder="Add tag (e.g. billing, Slack, lib)"
          className="block w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm"
        />
        <Button
          variant="secondary"
          disabled={!draft.trim() || add.isPending}
          onClick={submit}
        >
          {add.isPending ? "Adding…" : "Add"}
        </Button>
      </div>
      {add.error && (
        <p className="mt-2 text-xs text-red-700">
          {String((add.error as Error).message)}
        </p>
      )}
    </div>
  );
}

function isNonTerminalStatus(status: SourceRepoSummary["status"]): boolean {
  return status !== "indexed" && status !== "error";
}

function relativeFromNow(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

function statusTone(
  status: SourceRepoSummary["status"],
): "neutral" | "success" | "warn" | "danger" | "info" {
  switch (status) {
    case "indexed":
      return "success";
    case "error":
      return "danger";
    case "pending":
      return "neutral";
    case "cloning":
    case "analyzing":
    case "generating":
    case "indexing":
      return "info";
    default:
      return "neutral";
  }
}

type Panel = "none" | "edit" | "specs";

function RepoRow({ repo }: { repo: SourceRepoSummary }) {
  const [panel, setPanel] = useState<Panel>("none");
  const sync = useSyncRepo();
  const remove = useDeleteRepo();

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <Badge tone={statusTone(repo.status)}>{repo.status}</Badge>
            {repo.kind === "local" && <Badge tone="info">local</Badge>}
            <span className="font-medium text-zinc-900">{repo.name}</span>
          </div>
          <div className="mt-1 truncate font-mono text-xs text-zinc-500">
            {repo.url}
          </div>
          {repo.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {repo.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-700"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          <div className="mt-1 text-xs text-zinc-500">
            {repo.lastSyncedAt ? (
              <>
                Last synced{" "}
                {new Date(repo.lastSyncedAt).toLocaleString()}
                {repo.lastSyncedSha && (
                  <>
                    {" "}
                    · <code>{repo.lastSyncedSha.slice(0, 7)}</code>
                  </>
                )}{" "}
                · {repo.specCount} spec{repo.specCount === 1 ? "" : "s"}
              </>
            ) : (
              "Not yet synced"
            )}
            {repo.excludeFromDefault && (
              <>
                {" · "}
                <Badge tone="warn">excluded from defaults</Badge>
              </>
            )}
          </div>
          {repo.lastSyncStartedAt && (
            <div className="mt-0.5 text-xs text-zinc-500">
              {isNonTerminalStatus(repo.status) ? (
                <>
                  Started {relativeFromNow(repo.lastSyncStartedAt)} ago
                </>
              ) : (
                <>
                  Started{" "}
                  {new Date(repo.lastSyncStartedAt).toLocaleString()}
                </>
              )}
            </div>
          )}
          {repo.statusMessage && repo.status === "error" && (
            <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              {repo.statusMessage}
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            disabled={repo.specCount === 0}
            onClick={() => setPanel((p) => (p === "specs" ? "none" : "specs"))}
          >
            {panel === "specs"
              ? "Done"
              : `Specs${repo.specCount ? ` (${repo.specCount})` : ""}`}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setPanel((p) => (p === "edit" ? "none" : "edit"))}
          >
            {panel === "edit" ? "Done" : "Edit"}
          </Button>
          <Button
            variant="secondary"
            disabled={sync.isPending}
            onClick={() => sync.mutate(repo.id)}
          >
            {sync.isPending ? "…" : "Re-sync"}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (
                confirm(
                  `Remove ${repo.name}? Specs are deleted and the working clone is wiped.`,
                )
              ) {
                remove.mutate(repo.id);
              }
            }}
          >
            Remove
          </Button>
        </div>
      </div>

      {panel === "edit" && (
        <RepoEditor repo={repo} onClose={() => setPanel("none")} />
      )}
      {panel === "specs" && <RepoSpecs repoId={repo.id} />}
    </li>
  );
}

function RepoSpecs({ repoId }: { repoId: string }) {
  const specs = useRepoSpecs(repoId, true);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-4">
      {specs.isLoading && <p className="text-sm text-zinc-500">Loading…</p>}
      {specs.error && (
        <p className="text-sm text-red-700">
          {String((specs.error as Error).message)}
        </p>
      )}
      {specs.data && specs.data.specs.length === 0 && (
        <p className="text-sm text-zinc-500">No specs generated yet.</p>
      )}
      <ul className="divide-y divide-zinc-200">
        {(specs.data?.specs ?? []).map((s) => {
          const isOpen = openId === s.id;
          return (
            <li key={s.id} className="py-2">
              <button
                type="button"
                onClick={() => setOpenId((cur) => (cur === s.id ? null : s.id))}
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-zinc-900">
                    {s.title}
                  </span>
                  <span className="block text-xs text-zinc-500">
                    <code>{s.domain}</code> · {s.filePath}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-zinc-500">
                  {isOpen ? "Hide" : "View"}
                </span>
              </button>
              {isOpen && (
                <div className="mt-2">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-zinc-500">
                      {s.content.length} chars
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(s.content);
                      }}
                      className="text-xs text-zinc-600 underline-offset-2 hover:underline"
                    >
                      Copy
                    </button>
                  </div>
                  <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded border border-zinc-200 bg-white p-3 font-mono text-xs leading-relaxed text-zinc-800">
                    {s.content}
                  </pre>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RepoEditor({
  repo,
  onClose,
}: {
  repo: SourceRepoSummary;
  onClose: () => void;
}) {
  const update = useUpdateRepo();

  const [tagsText, setTagsText] = useState(repo.tags.join(", "));
  const [customPrompt, setCustomPrompt] = useState(repo.customPrompt ?? "");
  const [excludeFromDefault, setExcludeFromDefault] = useState(
    repo.excludeFromDefault,
  );
  const [category, setCategory] = useState<SourceRepoSummary["category"]>(
    repo.category,
  );
  const [teamId, setTeamId] = useState<string>(repo.teamId ?? "");
  const teams = useTeams();

  return (
    <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-4">
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-zinc-700">Category</span>
            <select
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as SourceRepoSummary["category"])
              }
              className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            >
              <option value="unknown">Unknown</option>
              <option value="service">Service</option>
              <option value="library">Library</option>
              <option value="frontend">Frontend</option>
              <option value="infra">Infra</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-zinc-700">Team</span>
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">— no team —</option>
              {(teams.data?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-zinc-700">Tags</span>
          <input
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="mobile, platform, legacy"
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
          />
          <span className="mt-1 block text-xs text-zinc-500">
            Comma-separated. Used for filtering and saved-query scoping.
          </span>
        </label>

        {repo.readmePrompt && (
          <div className="rounded border border-zinc-200 bg-white p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Detected from {repo.readmePromptSource}
              </span>
              {!customPrompt && (
                <button
                  type="button"
                  onClick={() => setCustomPrompt(repo.readmePrompt ?? "")}
                  className="text-xs text-zinc-700 underline-offset-2 hover:underline"
                >
                  Override
                </button>
              )}
            </div>
            <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap text-xs text-zinc-700">
              {repo.readmePrompt}
            </pre>
          </div>
        )}

        <label className="block text-sm">
          <span className="text-zinc-700">
            Custom prompt{" "}
            <span className="text-xs text-zinc-500">
              (optional — shown to the model when this repo is in the answer)
            </span>
          </span>
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="e.g. This repo is the legacy v2 API; flag answers about it as deprecated."
            className="mt-1 block w-full rounded-md border border-zinc-300 bg-white p-2 text-sm"
            rows={4}
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={excludeFromDefault}
            onChange={(e) => setExcludeFromDefault(e.target.checked)}
          />
          <span>Exclude from default-scope queries (Slack, default Ask)</span>
        </label>

        <ComponentMapPanel repoId={repo.id} />


        <div className="flex items-center gap-2">
          <Button
            disabled={update.isPending}
            onClick={() => {
              const tags = tagsText
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
              update.mutate(
                {
                  id: repo.id,
                  body: {
                    tags,
                    customPrompt: customPrompt.trim() ? customPrompt : null,
                    excludeFromDefault,
                    category,
                    teamId: teamId === "" ? null : teamId,
                  },
                },
                { onSuccess: () => onClose() },
              );
            }}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {update.error && (
            <span className="text-xs text-red-700">
              {String((update.error as Error).message)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function GithubOrgImportCard() {
  const integration = useGithubIntegration();
  const [loaded, setLoaded] = useState(false);
  const orgRepos = useGithubOrgRepos(loaded);
  const importMut = useImportGithubRepos();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [hideArchived, setHideArchived] = useState(true);
  const [hideForks, setHideForks] = useState(false);
  const [hideIngested, setHideIngested] = useState(false);

  const ready =
    !!integration.data?.configured && !!integration.data.hasPat;

  const visible = useMemo(() => {
    const repos = orgRepos.data?.repos ?? [];
    const q = filter.trim().toLowerCase();
    return repos.filter((r) => {
      if (hideArchived && r.archived) return false;
      if (hideForks && r.fork) return false;
      if (hideIngested && r.alreadyIngested) return false;
      if (!q) return true;
      return (
        r.fullName.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [orgRepos.data, filter, hideArchived, hideForks, hideIngested]);

  const selectable = visible.filter((r) => !r.alreadyIngested);
  const allVisibleSelected =
    selectable.length > 0 &&
    selectable.every((r) => selected.has(r.fullName));

  const toggle = (fullName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fullName)) next.delete(fullName);
      else next.add(fullName);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const r of selectable) next.delete(r.fullName);
      } else {
        for (const r of selectable) next.add(r.fullName);
      }
      return next;
    });
  };

  if (!ready) {
    return (
      <Card title="Import from GitHub organization">
        <p className="text-sm text-zinc-600">
          Bulk-pick repos from a GitHub org. Configure the org and a PAT in{" "}
          <a
            href="/settings"
            className="font-medium text-zinc-900 underline-offset-2 hover:underline"
          >
            Settings → GitHub integration
          </a>{" "}
          to enable this.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Import from GitHub organization">
      <p className="text-sm text-zinc-600">
        Pulls every repo in{" "}
        <Badge tone="info">{integration.data?.orgLogin}</Badge> visible to your
        PAT. Pick what to ingest — Oracus clones each one using the org PAT, so
        no per-repo token is needed.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={() => {
            setLoaded(true);
            if (loaded) void orgRepos.refetch();
          }}
          disabled={orgRepos.isFetching}
        >
          {orgRepos.isFetching
            ? "Loading…"
            : loaded
              ? "Reload list"
              : `Load repos from ${integration.data?.orgLogin}`}
        </Button>
        {orgRepos.data && (
          <span className="text-xs text-zinc-500">
            {orgRepos.data.repos.length} repo
            {orgRepos.data.repos.length === 1 ? "" : "s"} ·{" "}
            {orgRepos.data.repos.filter((r) => r.alreadyIngested).length} already
            ingested
          </span>
        )}
        {orgRepos.error && (
          <span className="text-xs text-red-700">
            {String((orgRepos.error as Error).message)}
          </span>
        )}
      </div>

      {orgRepos.data && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name or description…"
              className="flex-1 min-w-50 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-1.5 text-xs text-zinc-700">
              <input
                type="checkbox"
                checked={hideArchived}
                onChange={(e) => setHideArchived(e.target.checked)}
              />
              Hide archived
            </label>
            <label className="flex items-center gap-1.5 text-xs text-zinc-700">
              <input
                type="checkbox"
                checked={hideForks}
                onChange={(e) => setHideForks(e.target.checked)}
              />
              Hide forks
            </label>
            <label className="flex items-center gap-1.5 text-xs text-zinc-700">
              <input
                type="checkbox"
                checked={hideIngested}
                onChange={(e) => setHideIngested(e.target.checked)}
              />
              Hide already ingested
            </label>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={toggleAllVisible}
              disabled={selectable.length === 0}
              className="text-xs text-zinc-700 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-zinc-400"
            >
              {allVisibleSelected
                ? `Clear ${selectable.length} visible`
                : `Select all ${selectable.length} visible`}
            </button>
            <span className="text-xs text-zinc-500">
              {selected.size} selected
            </span>
          </div>

          <ul className="mt-2 max-h-96 divide-y divide-zinc-100 overflow-y-auto rounded-md border border-zinc-200">
            {visible.length === 0 && (
              <li className="py-4 text-center text-sm text-zinc-500">
                No repos match the current filters.
              </li>
            )}
            {visible.map((r) => (
              <OrgRepoRow
                key={r.fullName}
                repo={r}
                checked={selected.has(r.fullName)}
                onToggle={() => toggle(r.fullName)}
              />
            ))}
          </ul>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              disabled={selected.size === 0 || importMut.isPending}
              onClick={() => {
                importMut.mutate(
                  { fullNames: Array.from(selected) },
                  { onSuccess: () => setSelected(new Set()) },
                );
              }}
            >
              {importMut.isPending
                ? "Importing…"
                : `Import ${selected.size} selected`}
            </Button>
            {selected.size > 0 && !importMut.isPending && (
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-xs text-zinc-500 underline-offset-2 hover:underline"
              >
                Clear selection
              </button>
            )}
            {importMut.error && (
              <span className="text-xs text-red-700">
                {String((importMut.error as Error).message)}
              </span>
            )}
          </div>

          {importMut.data && (
            <div className="mt-3 rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-700">
              <div>
                <b className="text-emerald-700">{importMut.data.imported}</b>{" "}
                imported ·{" "}
                <b className="text-zinc-700">{importMut.data.skipped}</b>{" "}
                skipped (already present) ·{" "}
                <b className={importMut.data.failed > 0 ? "text-red-700" : ""}>
                  {importMut.data.failed}
                </b>{" "}
                failed
              </div>
              {importMut.data.results.some((r) => r.status === "failed") && (
                <ul className="mt-2 list-disc pl-5">
                  {importMut.data.results
                    .filter((r) => r.status === "failed")
                    .map((r) => (
                      <li key={r.fullName}>
                        <span className="font-mono">{r.fullName}</span>:{" "}
                        {r.message ?? "unknown error"}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function OrgRepoRow({
  repo,
  checked,
  onToggle,
}: {
  repo: GithubOrgRepo;
  checked: boolean;
  onToggle: () => void;
}) {
  const disabled = repo.alreadyIngested;
  return (
    <li className="flex items-start gap-3 px-3 py-2.5">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        className="mt-1"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={repo.htmlUrl}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-sm text-zinc-900 underline-offset-2 hover:underline"
          >
            {repo.fullName}
          </a>
          {repo.private && <Badge tone="warn">private</Badge>}
          {repo.archived && <Badge tone="neutral">archived</Badge>}
          {repo.fork && <Badge tone="neutral">fork</Badge>}
          {repo.alreadyIngested && <Badge tone="success">ingested</Badge>}
        </div>
        {repo.description && (
          <div className="mt-0.5 truncate text-xs text-zinc-600">
            {repo.description}
          </div>
        )}
        <div className="mt-0.5 text-[11px] text-zinc-500">
          {repo.defaultBranch && (
            <span className="font-mono">{repo.defaultBranch}</span>
          )}
          {repo.pushedAt && (
            <span className="ml-2">
              pushed {new Date(repo.pushedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}
